import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid,
  Typography, Chip, Divider, List, ListItem, ListItemText, Tooltip, CircularProgress,
  IconButton, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useSnackbar } from 'notistack';
import * as eqpAgentApi from '../../lib/api/eqpAgent';
import { getEqpStateLabel, getEqpStateChipColor, formatUptime } from './eqpState';
import { useCloseOnBack } from '../../hooks/useCloseOnBack';

dayjs.extend(relativeTime);

// 업데이트 상태(LAST_UPDATE_ST) -> 칩 색/라벨. PROTOCOL.md §7(v1.5).
const UPDATE_STATUS_META = {
  UPDATING: { label: '업데이트 중', color: 'info' },
  UPDATED: { label: '업데이트 완료', color: 'success' },
  UPDATE_FAILED: { label: '업데이트 실패', color: 'error' },
  REINSTALL_REQUIRED: { label: '수동 재설치 필요', color: 'warning' },
};

const StatItem = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
    <Typography variant="body2" component="div">{children}</Typography>
  </Box>
);

// PROTOCOL.md §6 - hello가 보낸 transport 값 → 화면 문구
const TRANSPORT_LABELS = {
  https: 'https 암호화',
  'http-private': '사내망 http',
  'http-allowed': 'http 강제 허용(경고)',
};

// 에이전트 상세 - 대기 큐/전송 오류/가동시간 같은 통신 상태와, 담당 장비 현황(machines API) +
// 드라이버 연결 현황(workers, 목록 응답에 이미 포함)을 한 다이얼로그에서 본다.
// 행을 누르면 여기부터 열리고, 수정은 이 안의 버튼으로 들어간다(모바일에서 누를 자리를 늘리지 않으려고).
const EqpAgentDetailDialog = ({ open, agentData, onClose, onEdit, onOpenInstaller, onOpenDiscovery }) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  useCloseOnBack(open, onClose);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ipLog, setIpLog] = useState([]);
  const [ipLogLoading, setIpLogLoading] = useState(false);
  // 서버 재조회 없이 바로 지워진 것처럼 보이게 하는 로컬 오버라이드 — 목록은 30초 주기로 어차피 다시 받는다.
  const [secWarnCleared, setSecWarnCleared] = useState(false);
  const [clearing, setClearing] = useState(false);
  // 자동 업데이트(§7, v1.5) — 매니페스트의 최신 버전과 비교해 보여준다.
  const [releaseInfo, setReleaseInfo] = useState(null);
  const [updateHistory, setUpdateHistory] = useState([]);

  useEffect(() => {
    if (!open) { setReleaseInfo(null); return; }
    eqpAgentApi.getAgentRelease()
      .then((res) => setReleaseInfo(eqpAgentApi.extractAgentRelease(res)))
      .catch(() => setReleaseInfo(null));
  }, [open]);

  useEffect(() => {
    if (!open || !agentData?.agentUuid) { setUpdateHistory([]); return; }
    eqpAgentApi.getAgentUpdateHistory(agentData.agentUuid, 5)
      .then((res) => setUpdateHistory(eqpAgentApi.extractUpdateHistoryList(res)))
      .catch(() => setUpdateHistory([]));
  }, [open, agentData?.agentUuid]);

  useEffect(() => {
    if (!open || !agentData?.agentUuid) { setMachines([]); return; }
    setLoading(true);
    eqpAgentApi.getAgentMachines(agentData.agentUuid)
      .then((res) => setMachines(eqpAgentApi.extractMachineList(res)))
      .catch(() => setMachines([]))
      .finally(() => setLoading(false));
  }, [open, agentData?.agentUuid]);

  useEffect(() => {
    if (!open || !agentData?.agentUuid) { setIpLog([]); return; }
    setIpLogLoading(true);
    eqpAgentApi.getAgentIpLog(agentData.agentUuid, 7)
      .then((res) => setIpLog(eqpAgentApi.extractIpLogList(res)))
      .catch(() => setIpLog([]))
      .finally(() => setIpLogLoading(false));
  }, [open, agentData?.agentUuid]);

  useEffect(() => {
    setSecWarnCleared(false); // 다이얼로그가 새로 열리면(또는 다른 에이전트면) 오버라이드를 다시 agentData 기준으로
  }, [open, agentData?.agentUuid]);

  if (!agentData) return null;

  const queueWarn = Number(agentData.queueLen) > 100;
  const machineName = (eqpUuid) => machines.find((m) => m.eqpUuid === eqpUuid)?.eqpNm;
  const secWarn = secWarnCleared ? null : agentData.secWarn;

  const handleCheckUpdateNow = () => {
    // 서버에서 즉시 밀어주는 경로가 없다(에이전트→PMS 방향 원칙, PROTOCOL §0) — 다음 config 폴링
    // (기본 60초)에서 에이전트가 스스로 받아 간다는 점을 안내만 한다.
    enqueueSnackbar('에이전트가 다음 설정 조회 때(기본 60초 이내) 새 버전을 확인합니다.', { variant: 'info' });
  };

  const handleClearSecWarn = async () => {
    if (!agentData?.agentUuid) return;
    setClearing(true);
    try {
      await eqpAgentApi.clearAgentSecWarn(agentData.agentUuid);
      setSecWarnCleared(true);
    } catch (e) {
      // 실패해도 조용히 - 경고는 그대로 남고, 목록 새로고침 때 다시 보인다
    } finally {
      setClearing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{agentData.agentNm} - 상세</Box>
        <IconButton aria-label="닫기" onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6} sm={3}>
            <StatItem label="대기 큐">
              <Box component="span" sx={queueWarn ? { color: 'error.main', fontWeight: 700 } : undefined}>
                {agentData.queueLen ?? 0}건
              </Box>
              {agentData.queueOldestDt && (
                <Typography variant="caption" color="text.secondary" component="div">
                  최초 {dayjs(agentData.queueOldestDt).fromNow()}
                </Typography>
              )}
            </StatItem>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatItem label="최근 전송 오류">
              {agentData.lastSendErr ? (
                <Tooltip title={agentData.lastSendErrDt ? dayjs(agentData.lastSendErrDt).format('YYYY-MM-DD HH:mm:ss') : ''}>
                  <Typography variant="body2" color="error.main" noWrap>{agentData.lastSendErr}</Typography>
                </Tooltip>
              ) : '-'}
            </StatItem>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatItem label="가동시간">{formatUptime(agentData.uptimeSec)}</StatItem>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatItem label="상태 수신">
              {agentData.statsDt ? dayjs(agentData.statsDt).fromNow() : '-'}
            </StatItem>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" gutterBottom>담당 장비</Typography>
        {loading ? (
          <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        ) : machines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">담당 장비가 없습니다.</Typography>
        ) : (
          <List dense disablePadding>
            {machines.map((m) => (
              <ListItem key={m.eqpUuid} disableGutters sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" color={getEqpStateChipColor(m.runSt, m.stale)} label={getEqpStateLabel(m.runSt, m.stale)} />
                <ListItemText
                  primary={`${m.eqpNm} (${m.eqpCd})`}
                  secondary={`${m.prgNo || '-'} · 가공수 ${m.partCnt ?? 0} · ${m.updDt ? dayjs(m.updDt).fromNow() : '-'}`}
                />
                {m.stale && (
                  <Tooltip title="데이터 수신 지연">
                    <WarningAmberIcon color="warning" fontSize="small" />
                  </Tooltip>
                )}
              </ListItem>
            ))}
          </List>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" gutterBottom>통신 연결</Typography>
        {(agentData.workers || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">연결 정보가 없습니다.</Typography>
        ) : (
          <List dense disablePadding>
            {agentData.workers.map((w, i) => (
              <ListItem key={`${w.eqpUuid}-${i}`} disableGutters sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" color={w.connected ? 'success' : 'default'} label={w.connected ? '연결됨' : '끊김'} />
                <ListItemText
                  primary={`${machineName(w.eqpUuid) || w.eqpUuid} · ${w.connTyp || '-'}`}
                  secondary={w.lastError || (w.lastReadAt ? `마지막 수신 ${dayjs(w.lastReadAt).fromNow()}` : '-')}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" gutterBottom>보안</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={4}>
            <StatItem label="통신 방식">
              {agentData.transport === 'http-allowed' ? (
                <Box component="span" sx={{ color: 'warning.main', fontWeight: 700 }}>
                  {TRANSPORT_LABELS['http-allowed']}
                </Box>
              ) : (TRANSPORT_LABELS[agentData.transport] || '-')}
            </StatItem>
          </Grid>
          <Grid item xs={12} sm={8}>
            <StatItem label="보안 경고">
              {secWarn ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" color="error" label="보안 경고" />
                  <Typography variant="body2" color="error.main">{secWarn}</Typography>
                  {agentData.secWarnDt && (
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(agentData.secWarnDt).format('YYYY-MM-DD HH:mm:ss')}
                    </Typography>
                  )}
                  <Button size="small" variant="outlined" onClick={handleClearSecWarn} disabled={clearing}>
                    확인함(경고 지우기)
                  </Button>
                </Box>
              ) : '-'}
            </StatItem>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 0 }}>자동 업데이트</Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={handleCheckUpdateNow}>
            지금 업데이트 확인
          </Button>
        </Box>
        <Grid container spacing={2} sx={{ mb: 1, mt: 0.5 }}>
          <Grid item xs={6} sm={3}>
            <StatItem label="현재 버전">{agentData.agentVer || '-'}</StatItem>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatItem label="최신 버전">
              {releaseInfo?.version || '-'}
              {releaseInfo?.version && agentData.agentVer && releaseInfo.version !== agentData.agentVer && (
                <Chip size="small" color="warning" label="업데이트 있음" sx={{ ml: 1 }} />
              )}
            </StatItem>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatItem label="자동 업데이트">{agentData.autoUpdateYn === 'N' ? '사용 안 함' : '사용'}</StatItem>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatItem label="버전 고정">{agentData.targetVer || '없음(최신 추종)'}</StatItem>
          </Grid>
          <Grid item xs={12}>
            <StatItem label="업데이트 상태">
              {agentData.lastUpdateSt ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    color={UPDATE_STATUS_META[agentData.lastUpdateSt]?.color || 'default'}
                    label={UPDATE_STATUS_META[agentData.lastUpdateSt]?.label || agentData.lastUpdateSt}
                  />
                  {agentData.lastUpdateMsg && (
                    <Typography variant="body2" color="text.secondary">{agentData.lastUpdateMsg}</Typography>
                  )}
                  {agentData.lastUpdateDt && (
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(agentData.lastUpdateDt).format('YYYY-MM-DD HH:mm:ss')}
                    </Typography>
                  )}
                </Box>
              ) : '-'}
            </StatItem>
          </Grid>
          {updateHistory.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                최근 업데이트 이력
              </Typography>
              <List dense disablePadding>
                {updateHistory.map((h, i) => (
                  <ListItem key={`${h.hisId || i}`} disableGutters sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      color={UPDATE_STATUS_META[h.status]?.color || 'default'}
                      label={UPDATE_STATUS_META[h.status]?.label || h.status}
                    />
                    <ListItemText
                      primary={h.msg || '-'}
                      secondary={`${h.agentVer || '-'} · ${h.crtDt ? dayjs(h.crtDt).format('YYYY-MM-DD HH:mm:ss') : '-'}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}
        </Grid>

        <Typography variant="caption" color="text.secondary" gutterBottom component="div">
          최근 7일 접속 IP 변경
        </Typography>
        {ipLogLoading ? (
          <Box sx={{ py: 1, textAlign: 'center' }}><CircularProgress size={18} /></Box>
        ) : ipLog.length === 0 ? (
          <Typography variant="body2" color="text.secondary">변경 이력이 없습니다.</Typography>
        ) : (
          <List dense disablePadding>
            {ipLog.map((row, i) => (
              <ListItem key={`${row.chgDt}-${i}`} disableGutters>
                <ListItemText
                  primary={`${row.oldIp || '-'} → ${row.newIp || '-'}`}
                  secondary={row.chgDt ? dayjs(row.chgDt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
        {onOpenDiscovery && (
          <Button startIcon={<SearchIcon />} onClick={() => onOpenDiscovery(agentData)}>
            주변 장비 찾기
          </Button>
        )}
        {onOpenInstaller && (
          <Button startIcon={<DownloadIcon />} onClick={() => onOpenInstaller(agentData)}>
            이 에이전트용 설치 파일
          </Button>
        )}
        <Button variant="contained" onClick={() => onEdit(agentData)}>수정</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EqpAgentDetailDialog;
